import { useCallback, useContext } from "react";
import { useRemoveFinancialDocumentFromImporterMutation } from "@api/financialDocument.api";
import { AccrualImporterContext } from "./accrualImporter.context";
import { AccrualImporterDocumentRow } from "./accrualImporter.types";

export function useRemoveAccrualImporterDocument(
    documentRow?: AccrualImporterDocumentRow,
) {
    const {
        removeDocumentRow,
        upsertDocumentRow,
        setRefreshSuppressed,
        setRefreshUnsuppressed,
    } = useContext(AccrualImporterContext);
    const removeFinancialDocument =
        useRemoveFinancialDocumentFromImporterMutation();

    const removeDocument = useCallback(async () => {
        if (!documentRow) {
            return false;
        }

        if (!documentRow.financialDocument) {
            removeDocumentRow(documentRow);
            return true;
        }

        removeDocumentRow(documentRow);
        setRefreshSuppressed();
        try {
            await removeFinancialDocument.mutateAsync(
                documentRow.financialDocument,
            );
        } catch {
            upsertDocumentRow(documentRow);
            setRefreshUnsuppressed();
            return false;
        }
        setRefreshUnsuppressed();
        return true;
    }, [
        documentRow,
        removeDocumentRow,
        upsertDocumentRow,
        removeFinancialDocument,
        setRefreshSuppressed,
        setRefreshUnsuppressed,
    ]);

    const removeDocuments = useCallback(
        async (rows: AccrualImporterDocumentRow[]) => {
            setRefreshSuppressed();
            const results = await Promise.allSettled(
                rows.map(async (row) => {
                    if (!row.financialDocument) {
                        removeDocumentRow(row);
                        return;
                    }

                    removeDocumentRow(row);
                    try {
                        await removeFinancialDocument.mutateAsync(
                            row.financialDocument,
                        );
                    } catch (error) {
                        upsertDocumentRow(row);
                        throw error;
                    }
                }),
            );
            setRefreshUnsuppressed();
            return results;
        },
        [
            removeDocumentRow,
            removeFinancialDocument,
            setRefreshSuppressed,
            setRefreshUnsuppressed,
            upsertDocumentRow,
        ],
    );

    return {
        removeDocument,
        removeDocuments,
        isRemoving: removeFinancialDocument.isPending,
    };
}
